import {TranslationObject} from '@colonist/i18n'
import {SectionBgView} from '../../../shared/scripts/ui_canvas_engine/SectionBgView'
import {SpriteView} from '../../../shared/scripts/ui_canvas_engine/SpriteView'
import {UIGameBankController} from '../../../shared/scripts/ui_game/UIGameBankController'
import {PIXI} from '@colonist/pixi'
import {SectionView} from '../../../shared/scripts/ui_canvas_engine/SectionView'
import {CardCountView} from '../../../shared/scripts/ui_canvas_engine/CardCountView'
import {UIGameProgressCardsToolTipView} from '../../../shared/scripts/ui_game/ui_game_tooltip/UIGameProgressCardsToolTipView'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables'
import {UIWGameManager} from './UIWGameManager'
import {SpriteExpansionArrowView} from '../../../shared/scripts/ui_canvas_engine/SpriteExpansionArrowView'
import {UIGameSpriteHandler} from '../../../shared/scripts/UIGameSpriteHandler'
import {Sprites} from '../../../shared/scripts/ui_game/UIGameImageDictionary'
import {CardEnum, MechanicDevelopmentCardsState, MechanicProgressCardsState, NestedReadonly} from '@colonist/game-states'

export class UIWGameBankController extends UIGameBankController {
    private bankExpansionArrow: SpriteExpansionArrowView
    private bankExpansionArrowContainer: SectionView

    constructor(uiGameManager: UIWGameManager,
                devCardsState: NestedReadonly<MechanicDevelopmentCardsState> | undefined,
                progressCardsState: NestedReadonly<MechanicProgressCardsState> | undefined) {
        super(uiGameManager, devCardsState, progressCardsState)

        this.initBankExpansionArrow()
        this.hideBankExpansionArrow()
        if(this.progressCardsState) this.initBankControllerToContainCommodities()
    }
    protected initBankContainer() {
        const canvas = this.uiGameManager.canvasController
        const x = canvas.appW - canvas.chatWidth
        const y = canvas.appH - canvas.itemSize * 5.5
        const w = canvas.chatWidth
        const h = canvas.itemSize

        this.container = new SectionBgView(x, y, w, h, canvas.app.stage)
        this.container.drawRoundedBg()
    }

    protected initBankIcon() {
        const bankIconTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bank)

        const bankIconContainerY = 0
        const bankIconContainerWidth = this.container.height * 0.75
        const bankIconContainerHeight = this.container.height - bankIconContainerY
        this.bankIconContainer = new SectionView(0, bankIconContainerY, bankIconContainerWidth, bankIconContainerHeight, this.container)

        const bankIconSize = this.container.height * 0.5
        const bankIconX = this.bankIconContainer.width * 0.3
        this.bankIcon = new SpriteView(bankIconX, 0, bankIconSize, bankIconSize, this.bankIconContainer, bankIconTexture)

        this.bankIcon.centerYInParent()

        const bankTipTitle: TranslationObject = {key: 'strings:game.cards.bank'}
        const bankTipBody: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
        this.bankIcon.setTip(bankTipTitle, [bankTipBody])
    }

    protected initializeCardContainer() {
        const removePadding = true
        this.cardCountViewContainer = new SectionView(this.cardH, 0, this.cardW * 5, this.cardH, this.container, removePadding)
    }

    protected initializeDevelopmentCardContainer(isProgressCard: boolean = false) {
        const isTradeView = false
        const hideBankCards = this.uiGameManager.gameController.gameSettings.hideBankCards

        this.developmentCardCountView = new CardCountView(this.uiGameManager, this.cardH + this.cardW * 5, 0, this.cardW, this.cardH, this.container, CardEnum.DevelopmentBack, this.textColor, isTradeView, hideBankCards)

        if(!isProgressCard) return

        const progressCardTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.card_progress_back)
        this.developmentCardCountView.topCard.view.texture = progressCardTexture
    }

    protected initializeCardDimension() {
        this.cardW = (this.container.width - this.container.height) / 6
        this.cardH = this.container.height
    }

    initBankControllerToContainCommodities() {
        this.showBankExpansionArrow()
        this.container.interactive = true
        this.container.on('pointerout', () => {
            if(!this.isBankInterfaceExpanded()) return
            this.contractBankInterface()
        })
        this.container.on('pointerover', () => {
            if(this.isBankInterfaceExpanded()) return
            this.expandBankInterface()
        })
        this.developmentCardCountView.destroy()
        const isProgressCard = true
        this.initializeDevelopmentCardContainer(isProgressCard)

        const showPlayerProgressCardCount = () => {
            this.showProgressCardToolTip()
        }
        const removePlayerProgressCardCount = () => {
            this.removeProgressCardToolTip()
        }
        this.developmentCardCountView.topCard.view.setHoverAction(showPlayerProgressCardCount, removePlayerProgressCardCount)
        this.update()
        if(UIUtilsEjsVariables.isUserOnMobile) this.registerMobileCKInteractions()
    }

    private registerMobileCKInteractions() {
        this.developmentCardCountView.topCard.view.setClickAction(() => {
            this.mobileProgressCardClicked()
        })
        this.container.on('tap', () => {
            this.isBankInterfaceExpanded() ? this.contractBankInterface() : this.expandBankInterface()
        })
        this.uiGameManager.canvasController.app.stage.interactive = true
        this.uiGameManager.canvasController.app.stage.on('tap', (event: PIXI.FederatedEvent) => {
            this.onMobileTap(event)
        })
    }

    private onMobileTap(event: PIXI.FederatedEvent) {
        const targetIsProgressCard = event.target == this.developmentCardCountView
        const targetIsProgressCardChild = event.target.parent == this.developmentCardCountView
        const shouldContractBankInterface = targetIsProgressCard || targetIsProgressCardChild
        if(shouldContractBankInterface) {
            this.contractBankInterface()
            return
        }

        const targetIsBankContainer = event.target == this.container
        const targetIsBankContainerChild = event.target.parent == this.container
        const targetIsBankContainerCard = event.target.parent?.parent == this.container
        const targetIsBankContainerCardSprite = event.target.parent?.parent?.parent == this.container
        const shouldRemoveProgressCardToolTip = targetIsBankContainer || targetIsBankContainerChild || targetIsBankContainerCard || targetIsBankContainerCardSprite
        if(shouldRemoveProgressCardToolTip) {
            this.removeProgressCardToolTip()
            return
        }

        // target is neither bank container nor progress card, so close each
        this.contractBankInterface()
        this.removeProgressCardToolTip()
    }

    private isBankInterfaceExpanded(): boolean {
        return this.bankIconContainer.parent == undefined
    }

    private contractBankInterface() {
        const scalingFactor = this.container.initialPosition.w / this.container.width

        this.container.scale.x *= scalingFactor
        this.cardCountViewContainer.scale.x *= 1 / scalingFactor
        this.developmentCardCountView.scale.x *= 1 / scalingFactor

        this.container.x = this.container.initialPosition.x
        this.showBankIcon()
        this.cardCountViewContainer.x = this.cardH
        this.developmentCardCountView.x = this.developmentCardCountView.initialPosition.x
        const allResourceCardsExcludingCommodities = 5
        this.showFirstXCards(allResourceCardsExcludingCommodities)
        this.showBankExpansionArrow()
    }

    private expandBankInterface() {
        const margin = this.uiGameManager.canvasController.margin
        const additionCardsToShow = 3
        const additionalCardWidth = this.cardW * additionCardsToShow
        const widthBeingRemoveByHidingBankIcon = this.bankIconContainer.width
        const widthBeingRemoveByHidingArrow = this.bankExpansionArrowContainer.width
        const widthBeingAddedToContainer = additionalCardWidth - widthBeingRemoveByHidingBankIcon - widthBeingRemoveByHidingArrow - margin * 2.42
        const extendedWidth = this.container.width + widthBeingAddedToContainer
        const scalingFactor = extendedWidth / this.container.width

        this.container.scale.x *= scalingFactor
        this.cardCountViewContainer.scale.x *= 1 / scalingFactor
        this.developmentCardCountView.scale.x *= 1 / scalingFactor

        this.container.x -= widthBeingAddedToContainer
        this.hideBankIcon()
        this.developmentCardCountView.x = this.container.initialPosition.w - this.developmentCardCountView.width - (margin / scalingFactor)
        this.cardCountViewContainer.x = margin / scalingFactor
        const allResourceCardsAndCommodities = 8
        this.showFirstXCards(allResourceCardsAndCommodities)
        this.hideBankExpansionArrow()
    }

    private showBankExpansionArrow() {
        if(this.bankExpansionArrowContainer.parent != undefined) return
        this.container.addChild(this.bankExpansionArrowContainer)
    }

    private showBankIcon() {
        if(this.bankIconContainer.parent != undefined) return
        this.container.addChild(this.bankIconContainer)
    }

    private hideBankIcon() {
        if(this.bankIconContainer.parent == undefined) return
        this.bankIconContainer.parent.removeChild(this.bankIconContainer)
    }

    private mobileProgressCardClicked() {
        const isToolTipActive = this.developmentCardProgressCardToolTipView != undefined
        isToolTipActive ? this.removeProgressCardToolTip() : this.showProgressCardToolTip()
    }

    private removeProgressCardToolTip() {
        if(this.developmentCardProgressCardToolTipView == undefined) return
        this.developmentCardProgressCardToolTipView.removeToolTip()
        this.developmentCardProgressCardToolTipView = undefined
    }

    private showProgressCardToolTip() {
        const cardCountView = this.developmentCardCountView.topCard
        const bankCards = this.progressCardsState?.bankProgressCards.cards ?? []
        const hideBankCards = this.uiGameManager.gameController.gameSettings.hideBankCards
        const title: TranslationObject = {key: 'strings:game.cards.progress.title.ProgressCards'}
        this.developmentCardProgressCardToolTipView = new UIGameProgressCardsToolTipView(cardCountView, title, bankCards, hideBankCards)
    }

    private initBankExpansionArrow() {
        const bankExpansionArrowContainerX = this.bankIcon.x * 0.2
        const bankExpansionArrowContainerY = this.bankIconContainer.y
        const bankExpansionArrowContainerW = this.container.width * 0.05
        const bankExpansionArrowContainerH = this.bankIconContainer.height
        this.bankExpansionArrowContainer = new SectionView(bankExpansionArrowContainerX, bankExpansionArrowContainerY, bankExpansionArrowContainerW, bankExpansionArrowContainerH, this.container, true)

        this.bankExpansionArrow = new SpriteExpansionArrowView(0, 0, this.bankExpansionArrowContainer)
        this.bankExpansionArrow.centerYInParent()
    }

    protected hideBankExpansionArrow() {
        if(this.bankExpansionArrowContainer.parent == undefined) return
        this.bankExpansionArrowContainer.parent.removeChild(this.bankExpansionArrowContainer)
    }
}
